import { useCallback, useEffect, useMemo, useState } from 'react'
import { getContratosUsuario } from '@/common/utils/services/usuarios.service'
import { getContrato, getIdUsuario } from '@/auth/auth.utils'
import { useAuthStore } from '@/common/zustand/store/auth.store'
import { useDebouncedValue } from '@/common/utils/hooks/useDebouncedValue'
import { ordenarCuentas } from '../util/ordenarCuentas.js'
import { filtrarCuentas } from '../util/filtrarCuentas.js'
import { ESTADO_PENDIENTE, MENSAJES_GRILLA, PAGE_SIZE_INICIAL } from '../constants/textos.js'

/**
 * Las solicitudes de asociación todavía no aprobadas vienen mezcladas con las
 * cuentas activas en el mismo endpoint. No se puede ingresar a una cuenta que
 * no está aprobada, así que quedan fuera de esta grilla.
 */
const esCuentaDisponible = (cuenta) => cuenta?.estado !== ESTADO_PENDIENTE

/**
 * Carga las cuentas del usuario y resuelve orden, búsqueda y paginación.
 *
 * El contrato activo sale del store para que, al cambiar de cuenta, la grilla
 * se reordene sola sin volver a pedir los datos (criterio 6).
 */
export const useCuentasSeleccion = () => {
  const contratoStore = useAuthStore((s) => s.contrato)
  const contratoActivo = contratoStore ?? getContrato()

  const [cuentas, setCuentas] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const [searchValue, setSearchValue] = useState('')
  const busquedaDebounced = useDebouncedValue(searchValue)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_INICIAL)

  const fetchCuentas = useCallback(async () => {
    const idUsuario = getIdUsuario()

    if (!idUsuario) {
      setError(MENSAJES_GRILLA.ERROR.TITULO)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    const resultado = await getContratosUsuario(Number(idUsuario))

    if (resultado.code === 200) {
      setCuentas((Array.isArray(resultado.data) ? resultado.data : []).filter(esCuentaDisponible))
    } else {
      setError(resultado.message || MENSAJES_GRILLA.ERROR.TITULO)
      setCuentas([])
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    fetchCuentas()
  }, [fetchCuentas])

  // Criterio 8: al cambiar la búsqueda se vuelve a la primera página.
  useEffect(() => {
    setCurrentPage(1)
  }, [busquedaDebounced])

  // Al cambiar de cuenta, la nueva activa pasa a ser la primera de la grilla:
  // volver a la página 1 es lo único que hace falta para que se vea.
  useEffect(() => {
    setCurrentPage(1)
  }, [contratoActivo])

  const cuentasOrdenadas = useMemo(() => ordenarCuentas(cuentas, contratoActivo), [cuentas, contratoActivo])

  const cuentasFiltradas = useMemo(
    () => filtrarCuentas(cuentasOrdenadas, busquedaDebounced),
    [cuentasOrdenadas, busquedaDebounced]
  )

  const cuentasPaginadas = useMemo(() => {
    const desde = (currentPage - 1) * pageSize
    return cuentasFiltradas.slice(desde, desde + pageSize)
  }, [cuentasFiltradas, currentPage, pageSize])

  const headers = useMemo(() => ({ 'x-total-count': cuentasFiltradas.length }), [cuentasFiltradas])

  const onPageChange = useCallback(
    ({ page, pageSize: nuevoPageSize }) => {
      if (nuevoPageSize && nuevoPageSize !== pageSize) {
        setPageSize(nuevoPageSize)
        setCurrentPage(1)
        return
      }
      if (page) setCurrentPage(page)
    },
    [pageSize]
  )

  const cuentaActiva = useMemo(
    () => cuentasOrdenadas.find((cuenta) => Number(cuenta?.contrato) === Number(contratoActivo)) ?? null,
    [cuentasOrdenadas, contratoActivo]
  )

  return {
    cuentas: cuentasPaginadas,
    cuentaActiva,
    contratoActivo,
    headers,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    // El filtro aplicado es el debounced: el empty state tiene que hablar del
    // resultado que se está mostrando, no de lo que el usuario está tipeando.
    hayFiltroAplicado: busquedaDebounced.trim().length > 0,
    onPageChange,
    refrescarCuentas: fetchCuentas,
  }
}
